"use client";

import { Box, Stack, Typography, useTheme } from "@mui/material";
import { motion, useScroll, useTransform } from "motion/react";

import { fadeInAnimation } from "@/helpers/fadeInAnimation";
import { assetPaths } from "@/lib/assetPaths";
import { useMessages } from "@/lib/i18n/useMessages";
import Image from "next/image";
import { RefObject } from "react";
import { SLButtonGroupAnrufenBuchen } from "../../buttons/SLButtonGroupAnrufenBuchen";
import { SLHtmlFromLangFile } from "./SLHtmlFromLangFile";

export const SLHomeHeader = ({
  containerRef,
  isMobile,
  video,
}: {
  containerRef: RefObject<HTMLDivElement | null>;
  isMobile: boolean;
  video: React.ReactNode;
}) => {
  const theme = useTheme();
  const home = useMessages().home;

  const animatedBoxWith = "50vw";

  // Use section-specific scroll progress
  const { scrollYProgress: sectionProgress } = useScroll({
    target: containerRef,
    offset: ["start start", "end end"],
  });

  const width = useTransform(
    sectionProgress,
    [0, 0.3],
    [animatedBoxWith, "98vw"],
  );

  const logoSizeVw = isMobile ? 30 : 18;

  return (
    /* Header container - Container that limits sticky behavior - ends exactly when width reaches 100% */

    <Box
      component={motion.header}
      initial={{
        paddingLeft: "0vw",
      }}
      animate={{
        paddingLeft: "1vw",
      }}
      transition={{
        duration: 0.2,
        delay: 2.8,
      }}
    >
      <Box
        component={motion.div}
        initial={{
          paddingRight: "0vw",
          width: isMobile ? "100vw" : "calc(100vw - 18px)",
        }}
        animate={{
          paddingRight: "1vw",
          width: isMobile ? "98.5vw" : "calc(99vw - 18px)",
        }}
        transition={{
          duration: 0.2,
          delay: 2.8,
        }}
        ref={containerRef}
        sx={{
          height: isMobile ? "auto" : `200vh`,

          overflow: "clip",

          zIndex: 1000,
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Stack
          direction={{ xs: "column", sm: "row" }}
          sx={{
            position: isMobile ? "relative" : "sticky",
            top: 0,
            left: 0,
            m: 0,
            width: { xs: "100%", sm: "200vw" },
            zIndex: 10,
            overflow: "visible",
            justifyContent: isMobile ? "space-between" : "unset",
            height: isMobile ? "auto" : "unset",
          }}
        >
          {/* Animated box */}
          <motion.div
            transition={{ duration: 1, delay: 2, ease: "circInOut" }}
            initial={{
              height: "110vh",
              width: "110vw",
              paddingBottom: 0,
              paddingRight: 0,
              paddingTop: 0,
              marginLeft: -30,
              marginTop: 0,
              scale: 2.5,
            }}
            animate={
              isMobile
                ? {
                    scale: 1,
                    width: "100%",
                    height: "98vw",
                    paddingBottom: "0vw",
                    paddingRight: "0vw",
                    paddingTop: "1vw",
                    marginLeft: 0,
                    marginTop: 0,
                  }
                : {
                    scale: 1,
                    height: "98vh",
                    width: "48vw",
                    paddingBottom: "1vw",
                    paddingRight: "1vw",
                    paddingTop: "1vw",
                    marginLeft: 0,
                    marginTop: 0,
                  }
            }
            style={{
              width: isMobile ? "100%" : width,
              position: "relative",
              zIndex: 101,
            }}
          >
            <Stack
              justifyContent="center"
              id="video-container"
              alignItems="center"
              sx={{
                height: "100%",
                width: "100%",
                position: "relative",
                overflow: "clip",
                borderRadius: `${theme.shape.borderRadius}px`,
                backgroundColor: theme.palette.primary.main,
              }}
            >
              <Box
                component={motion.div}
                transition={{ duration: 1, delay: 2, ease: "circInOut" }}
                sx={{
                  width: `${logoSizeVw}vw`,
                  height: `${logoSizeVw}vw`,
                  position: "relative",
                  zIndex: 100,
                  overflow: "hidden",
                }}
              >
                <Box
                  component={motion.div}
                  transition={{ duration: 1, ease: "easeIn" }}
                  sx={{
                    position: "absolute",
                  }}
                  initial={{
                    left: 0,
                    bottom: `-${logoSizeVw}vw`,
                  }}
                  animate={{
                    left: 0,
                    bottom: 0,
                  }}
                  width={`${logoSizeVw}vw`}
                  height={`${logoSizeVw}vw`}
                >
                  <Image
                    src={assetPaths.logos.white}
                    alt="Solving Legal"
                    fill={true}
                  />
                </Box>
              </Box>
              {video}
            </Stack>
          </motion.div>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              height: isMobile ? "fit-content" : "98vh",
              width: isMobile ? "100%" : "48vw",
              p: isMobile ? 2 : 4,

              justifyContent: "flex-end",
              gap: 10,
            }}
          >
            <Typography
              component="p"
              fontWeight={theme.typography.fontWeightLight}
              variant="h2"
              color={theme.palette.text.primary}
            >
              <SLHtmlFromLangFile htmlString={home.header.title} />
            </Typography>
            <Box component={motion.div} {...fadeInAnimation({ delay: 0.3 })}>
              <Typography
                component="p"
                fontWeight={theme.typography.fontWeightLight}
                variant="h6"
                color={theme.palette.text.primary}
              >
                <SLHtmlFromLangFile htmlString={home.header.subtitle} />
              </Typography>
            </Box>
            <Box component={motion.div} {...fadeInAnimation({ delay: 0.5 })}>
              <SLButtonGroupAnrufenBuchen />
            </Box>
          </Box>
        </Stack>
      </Box>
    </Box>
  );
};
